/**
 * DMV Soundstage Logo System - Simplified
 * 
 * Logo Variants:
 * 1. FULL LOGO - Full "DMV SOUNDSTAGE" with soundwave (landing, marketing, auth)
 * 2. ICON LOGO - Compact "DMV" badge (navbar, favicon, compact areas)
 */

// Logo asset paths
const DEFAULT_LOGOS = {
  fullLogo: '/logos/full-logo.png',
  iconLogo: '/logos/icon-logo.png'
};

// Logo configurations with metadata
// Full logo is wide (roughly 3:1 aspect ratio), so we specify width and let height auto
export const LOGO_CONFIG = {
  fullLogo: {
    src: process.env.REACT_APP_FULL_LOGO || DEFAULT_LOGOS.fullLogo,
    alt: 'DMV Soundstage - Your Sound, Your Stage',
    description: 'Full brand logo with soundwave for landing page and marketing',
    aspectRatio: 'wide', // Indicates this is a wide logo, not square
    sizes: {
      sm: { width: 200 },   // Mobile small
      md: { width: 260 },   // Mobile/Tablet
      lg: { width: 320 },   // Tablet/Desktop
      xl: { width: 400 },   // Desktop large
      '2xl': { width: 480 } // Desktop hero
    }
  },
  iconLogo: {
    src: process.env.REACT_APP_ICON_LOGO || DEFAULT_LOGOS.iconLogo,
    alt: 'DMV Soundstage',
    description: 'Compact DMV icon for navbar and compact areas',
    aspectRatio: 'square', // Square logo
    sizes: {
      xs: { width: 32, height: 32 },
      sm: { width: 40, height: 40 },
      md: { width: 48, height: 48 },
      lg: { width: 56, height: 56 },
      xl: { width: 64, height: 64 }
    }
  }
};

// Routing rules - which logo to use where
export const LOGO_ROUTES = {
  // Icon Logo locations (compact)
  navbar: 'iconLogo',
  favicon: 'iconLogo',
  mobileAppIcon: 'iconLogo',
  sidebarCollapsed: 'iconLogo',
  playerControls: 'iconLogo',
  videoWatermark: 'iconLogo',
  profileBadge: 'iconLogo',
  socialSharePreview: 'iconLogo',
  
  // Full Logo locations (prominent)
  landingHero: 'fullLogo',
  landingHeader: 'fullLogo',
  marketingBanner: 'fullLogo',
  appStoreScreenshot: 'fullLogo',
  authPage: 'fullLogo',
  footer: 'fullLogo',
  reelsPage: 'fullLogo',
  musicPage: 'fullLogo',
  videosPage: 'fullLogo',
  createEntry: 'fullLogo',
  emptyMediaState: 'fullLogo'
};

/**
 * Get logo configuration for a specific location
 * @param {string} location - The UI location (e.g., 'navbar', 'landingHero')
 * @returns {object} Logo configuration object
 */
export function getLogoForLocation(location) {
  const logoKey = LOGO_ROUTES[location];
  if (!logoKey) {
    console.warn(`[LogoConfig] Unknown location: ${location}, falling back to iconLogo`);
    return LOGO_CONFIG.iconLogo;
  }
  return LOGO_CONFIG[logoKey];
}

/**
 * Get logo by type directly
 * @param {string} type - Logo type ('fullLogo', 'iconLogo')
 * @returns {object} Logo configuration object
 */
export function getLogo(type) {
  return LOGO_CONFIG[type] || LOGO_CONFIG.iconLogo;
}

/**
 * Preload all logo assets for faster rendering
 */
export function preloadLogos() {
  Object.values(LOGO_CONFIG).forEach(logo => {
    const img = new Image();
    img.src = logo.src;
  });
}

// Legacy aliases for backward compatibility
export const LOGO_ALIASES = {
  fullBrandLogo: 'fullLogo',
  playLogo: 'fullLogo',
  badgeLogo: 'iconLogo'
};

// Export default logos for direct import
export default LOGO_CONFIG;
